// TimelineView — faithful React port of the standalone SVG timeline renderer
// (examples/timeline-demo). Pure over a backend-neutral `TimelineSpec`: it knows
// nothing about RDF/QROS, only how to draw lanes / eras / tracks / events.
//
// Generalized from the original calendar-only renderer to support two axes:
//   * calendar — `axis.kind` omitted; positions are "YYYY-MM-DD" strings.
//   * ordinal  — `axis.kind === 'ordinal'`; positions are story-time numbers.
// The only date-aware seam is `toNum`; everything downstream is numeric. Derived
// (inferred) segments render dashed with an "open below" arrow.
//
// Theme colors use the shadcn CSS tokens (--foreground/--muted-foreground/
// --border) so the chart inverts in dark mode; the era palette stays explicit hex
// (semantic comic colors, theme-independent).

import { useMemo, type ReactElement } from 'react';
import type { Pos, Track, TimelineSpec } from '@/lib/timelines/types';
import { tokenRgb } from '@/lib/tokens';

const R = 30;
const W = 1140;
const ROW = 26;
// A row whose label carries a second line (its issue) needs room for both.
const ROW_TWO_LINES = 40;
const LANE_PAD = 20;
const LANE_HEAD = 56;
const TOP = 86;
// Left gutter holds the row labels. Ordinal (story-time) labels are long story
// titles + a cover-date annotation, so they need a wider gutter than the short
// affiliation member names; calendar stays at 196 so that view is unchanged.
const L_CALENDAR = 196;
const L_ORDINAL = 270;

const INK = 'hsl(var(--foreground))';
const MUTED = 'hsl(var(--muted-foreground))';
const GRID = 'hsl(var(--border))';
// Chart colours from the theme's tokens (lib/tokens.ts). Getters, so each
// read happens at render and follows the theme; the call sites read them as
// plain strings.
const CHART = {
  get a() { return tokenRgb('--tres-chart-1'); },
  get b() { return tokenRgb('--tres-chart-2'); },
  get c() { return tokenRgb('--tres-chart-3'); },
  get cross() { return tokenRgb('--tres-chart-4'); },
  get bar() { return tokenRgb('--tres-chart-5'); },
  get muted() { return tokenRgb('--tres-viz-muted'); },
};
const ERA: Record<string, string> = {
  get 'era-a'() { return CHART.a; },
  get 'era-b'() { return CHART.b; },
  get 'era-c'() { return CHART.c; },
};

function parseDate(s: string): number {
  const p = s.split('-');
  const y = Number(p[0] ?? 0);
  const m = Number(p[1] ?? 1);
  const d = Number(p[2] ?? 1);
  return y * 12 + (m - 1) + (d - 1) / 31;
}

export function TimelineView({ spec }: { spec: TimelineSpec }) {
  const { svg, width, height } = useMemo(() => buildSvg(spec), [spec]);
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card px-5 py-4">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ display: 'block', height: 'auto', maxWidth: width, margin: '0 auto' }}
      >
        {svg}
      </svg>
    </div>
  );
}

type Built = { svg: ReactElement[]; width: number; height: number };

function buildSvg(spec: TimelineSpec): Built {
  // `beats` signal the arc/precedes view (single publication axis, story-order
  // nodes, backward-arc highlight); per-lane axes signal the two-scale comparison;
  // otherwise the classic single-axis timeline.
  if (spec.view === 'arcs' || spec.beats) return buildArcs(spec);
  return spec.lanes.some((l) => l.axis != null) ? buildComparison(spec) : buildLegacy(spec);
}

// Arc view: one publication axis; each story beat is a numbered node at its cover
// date; `precedes` edges arc beneath, gold where they run BACKWARD against print
// time (story order ≠ publication order on a single line).
function buildArcs(spec: TimelineSpec): Built {
  const W = 1380;
  const Lm = 60;
  const R = 60;
  const plotW = W - Lm - R;
  const H = 360;
  const yTop = 44;
  const yBot = 308;
  const BASE = 156;

  const beats = spec.beats ?? [];
  const precedes = spec.precedes ?? [];
  const toMonth = (s: string) => {
    const [y, m] = s.split('-').map(Number);
    return (y ?? 0) * 12 + ((m ?? 1) - 1);
  };
  const t0 = toMonth(spec.axis.from as string);
  const t1 = toMonth(spec.axis.to as string);
  const x = (date: string) => Lm + ((toMonth(date) - t0) / (t1 - t0)) * plotW;
  const byRank = (r: number) => beats.find((b) => b.rank === r);

  const NODE = CHART.a;
  const STAR = CHART.c;
  const FWD = CHART.b;
  const BACK = CHART.cross;
  const CARD = 'hsl(var(--card))';
  const MONTHS = 'JFMAMJJASOND';

  const els: ReactElement[] = [];
  let k = 0;
  const key = () => `a${k++}`;

  // axis: month ticks + year labels, then the baseline
  for (let n = Math.ceil(t0); n <= t1; n++) {
    const dt = `${Math.floor(n / 12)}-${String((n % 12) + 1).padStart(2, '0')}-01`;
    const xx = x(dt);
    const isYear = n % 12 === 0;
    els.push(
      <line
        key={key()}
        x1={xx}
        y1={yTop}
        x2={xx}
        y2={yBot}
        stroke={GRID}
        strokeWidth={1}
        opacity={isYear ? 1 : 0.45}
      />,
    );
    els.push(
      <text
        key={key()}
        x={xx + 3}
        y={yBot + 14}
        fill={MUTED}
        fontSize={isYear ? 12 : 10}
        fontWeight={isYear ? 600 : undefined}
        opacity={isYear ? 1 : 0.7}
      >
        {isYear ? String(Math.floor(n / 12)) : (MONTHS[n % 12] ?? '')}
      </text>,
    );
  }
  els.push(<line key={key()} x1={Lm} y1={BASE} x2={W - R} y2={BASE} stroke={GRID} strokeWidth={1.5} />);

  // arcs sorted by span so they nest cleanly (longest deepest)
  const span = (p: [number, number]) => {
    const a = byRank(p[0]);
    const b = byRank(p[1]);
    return a && b ? Math.abs(x(b.date) - x(a.date)) : 0;
  };
  const order = [...precedes].sort((a, b) => span(a) - span(b));
  const depthRank = new Map(order.map((p, i) => [p.join('-'), i]));
  for (const p of precedes) {
    const A = byRank(p[0]);
    const B = byRank(p[1]);
    if (!A || !B) continue;
    const xa = x(A.date);
    const xb = x(B.date);
    const back = xb < xa;
    const col = back ? BACK : FWD;
    const peak = BASE + 40 + (depthRank.get(p.join('-')) ?? 0) * 22;
    els.push(
      <path
        key={key()}
        d={`M${xa},${BASE + 8} C${xa},${peak} ${xb},${peak} ${xb},${BASE + 12}`}
        fill="none"
        stroke={col}
        strokeWidth={back ? 2.6 : 1.9}
        opacity={back ? 0.95 : 0.7}
      />,
    );
    const d = xb >= xa ? 1 : -1;
    els.push(
      <path
        key={key()}
        d={`M${xb},${BASE + 12} l${-d * 6},6 l${d * 12},0 z`}
        fill={col}
        opacity={back ? 0.95 : 0.7}
      />,
    );
  }

  // numbered nodes; first appearance gets the star color
  for (const b of beats) {
    const xx = x(b.date);
    els.push(<circle key={key()} cx={xx} cy={BASE} r={13} fill={b.first ? STAR : NODE} />);
    els.push(
      <text key={key()} x={xx} y={BASE + 4} textAnchor="middle" fill={CARD} fontSize={12} fontWeight={700}>
        {b.rank}
      </text>,
    );
    els.push(
      <text key={key()} x={xx} y={BASE - 28} textAnchor="middle" fill={INK} fontSize={12} fontWeight={600}>
        {b.code}
      </text>,
    );
    els.push(
      <text key={key()} x={xx} y={BASE - 16} textAnchor="middle" fill={MUTED} fontSize={10}>
        {b.mon}
      </text>,
    );
  }

  return { svg: els, width: W, height: H };
}

function makeScale(axis: { kind?: string; from: Pos; to: Pos }, gut: number, plotW: number) {
  const kind = axis.kind ?? 'calendar';
  const toNum = (v: Pos): number => (kind === 'ordinal' ? Number(v) : parseDate(String(v)));
  const t0 = toNum(axis.from);
  const t1 = toNum(axis.to);
  return (v: Pos) => gut + ((toNum(v) - t0) / (t1 - t0)) * plotW;
}

// Comparison view: each lane is a horizontal axis on its own scale; every story is
// a colored dot in both lanes (same color), linked by a connector so the reorder
// between publication order and story order reads as crossing colored paths.
function buildComparison(spec: TimelineSpec): Built {
  const W = 1380;
  const GUT = 150;
  const R = 44;
  const plotW = W - GUT - R;
  const TOP = 70;
  const BASE_GAP = 184;
  const laneBaseY = (i: number) => TOP + 30 + i * BASE_GAP;
  const H = laneBaseY(spec.lanes.length - 1) + 96;

  const els: ReactElement[] = [];
  let k = 0;
  const key = () => `c${k++}`;
  const trackKey = (t: Track) => t.id ?? t.label;

  // connectors first (under the dots), matching the same id across lanes
  const byId = new Map<string, { x: number; y: number; color: string }[]>();

  spec.lanes.forEach((lane, i) => {
    const axis = lane.axis ?? spec.axis;
    const xf = makeScale(axis, GUT, plotW);
    const by = laneBaseY(i);
    const labelsAbove = i === 0;

    // lane name in the left gutter
    els.push(
      <text key={key()} x={GUT - 16} y={by + 4} fill={INK} fontSize={13} fontWeight={600} textAnchor="end">
        {lane.label}
      </text>,
    );
    // baseline + direction hint
    els.push(<line key={key()} x1={GUT} y1={by} x2={W - R} y2={by} stroke={GRID} strokeWidth={1.5} />);
    els.push(
      <text key={key()} x={W - R} y={by - 6} fill={MUTED} fontSize={9.5} textAnchor="end" opacity={0.8}>
        later →
      </text>,
    );
    // year ticks for a calendar lane (gives the publication gap real context)
    if ((axis.kind ?? 'calendar') === 'calendar') {
      const y0 = Number(String(axis.from).slice(0, 4));
      const y1 = Number(String(axis.to).slice(0, 4));
      for (let yr = y0; yr <= y1; yr++) {
        const xx = xf(`${yr}-01-01`);
        els.push(<line key={key()} x1={xx} y1={by - 4} x2={xx} y2={by + 4} stroke={GRID} strokeWidth={1} />);
        if (yr % 5 === 0) {
          els.push(
            <text key={key()} x={xx} y={by + (labelsAbove ? 16 : -10)} fill={MUTED} fontSize={9} textAnchor="middle" opacity={0.7}>
              {yr}
            </text>,
          );
        }
      }
    }

    // dots + labels, with light vertical stagger to avoid label collisions
    const placed: { x: number; y: number }[] = [];
    for (const t of spec.tracks.filter((tr) => tr.laneId === lane.id)) {
      const seg = t.segments[0];
      if (!seg) continue;
      const x = xf(seg.from);
      const color = t.color ?? CHART.bar;
      const derived = !!seg.derived;
      let pts = byId.get(trackKey(t));
      if (!pts) {
        pts = [];
        byId.set(trackKey(t), pts);
      }
      pts.push({ x, y: by, color });
      // dot — hollow dashed ring when the position is inferred (no canonical date)
      els.push(
        derived ? (
          <circle key={key()} cx={x} cy={by} r={6.5} fill="none" stroke={color} strokeWidth={2} strokeDasharray="3 2" />
        ) : (
          <circle key={key()} cx={x} cy={by} r={6.5} fill={color} />
        ),
      );
      // primary label (story name or YYYY-MM), secondary (pub year) under it
      let ly = labelsAbove ? by - 16 : by + 22;
      const step = labelsAbove ? -13 : 13;
      while (placed.some((p) => Math.abs(p.x - x) < 86 && Math.abs(p.y - ly) < 12)) ly += step;
      placed.push({ x, y: ly });
      els.push(
        <text key={key()} x={x} y={ly} fill={INK} fontSize={11} textAnchor="middle">
          {t.label}
        </text>,
      );
      if (t.issue) {
        els.push(
          <text key={key()} x={x} y={ly + (labelsAbove ? -11 : 12)} fill={MUTED} fontSize={9.5} textAnchor="middle">
            {t.issue}
          </text>,
        );
      }
      if (t.tag) {
        els.push(
          <text
            key={key()}
            x={x}
            y={ly + (labelsAbove ? -22 : 25)}
            fill={color}
            fontSize={9}
            fontWeight={700}
            textAnchor="middle"
            style={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}
          >
            {t.tag}
          </text>,
        );
      }
    }
  });

  // connectors: link the same story across the two lanes, colored per story
  const connectors: ReactElement[] = [];
  for (const pts of byId.values()) {
    if (pts.length < 2) continue;
    const a = pts[0];
    const b = pts[1];
    if (!a || !b) continue;
    const y0 = a.y + 7;
    const y1 = b.y - 7;
    const mid = (a.y + b.y) / 2;
    connectors.push(
      <path
        key={key()}
        d={`M${a.x},${y0} C${a.x},${mid} ${b.x},${mid} ${b.x},${y1}`}
        fill="none"
        stroke={a.color}
        strokeWidth={2}
        opacity={0.8}
      />,
    );
  }

  return { svg: [...connectors, ...els], width: W, height: H };
}

function buildLegacy(spec: TimelineSpec): Built {
  const kind = spec.axis.kind ?? 'calendar';
  const toNum = (v: Pos): number => (kind === 'ordinal' ? Number(v) : parseDate(String(v)));
  const L = kind === 'ordinal' ? L_ORDINAL : L_CALENDAR;
  const plotW = W - L - R;

  const t0 = toNum(spec.axis.from);
  const t1 = toNum(spec.axis.to);
  const x = (v: Pos) => L + ((toNum(v) - t0) / (t1 - t0)) * plotW;

  const byLane = (id: string) => spec.tracks.filter((t) => t.laneId === id);

  // Layout: lanes stacked, member rows within each. Maps keep the strict
  // index-access checks happy; the algorithm guarantees every key is present.
  // Rows are keyed by track IDENTITY, not by label. The validity derivation
  // labels every track by its SUBJECT, so several statements about one entity
  // arrive as several tracks sharing one label. Keying by label meant each of
  // them overwrote the previous one's row position while the cursor still
  // advanced — same-subject rows drew on top of each other and left a blank
  // row where each overwritten one should have been.
  const idOf = (t: Track, i: number) => t.id ?? `${t.label}#${i}`;
  const rowHeight = (t: Track) => (t.issue ? ROW_TWO_LINES : ROW);
  // The band above the plot holds event labels; with no events it is empty.
  const top = spec.events.length > 0 ? TOP : 44;
  let y = top;
  const laneY = new Map<string, number>();
  const laneBottom = new Map<string, number>();
  const rowY = new Map<string, Map<string, number>>();
  for (const lane of spec.lanes) {
    laneY.set(lane.id, y);
    y += LANE_HEAD;
    const rows = new Map<string, number>();
    byLane(lane.id).forEach((t, i) => {
      rows.set(idOf(t, i), y);
      y += rowHeight(t);
    });
    rowY.set(lane.id, rows);
    laneBottom.set(lane.id, y);
    y += LANE_PAD;
  }
  // Room under the plot for the tick labels, and the axis name under those.
  const H = y + (spec.axis.label ? 44 : 26);
  const laneTop = (id: string) => laneY.get(id) ?? 0;
  const rowTop = (laneId: string, trackId: string) => rowY.get(laneId)?.get(trackId) ?? 0;

  const eraFor = (laneId: string, from: Pos) =>
    spec.eras.find(
      (e) =>
        e.laneId === laneId && toNum(e.from) <= toNum(from) + 1e-9 && toNum(from) < toNum(e.to) + 1e-9,
    );

  const els: ReactElement[] = [];
  let k = 0;
  const key = () => `e${k++}`;

  // ---- axis ----
  const y0 = top - 14;
  // The plot ends above its tick labels, and above the axis name when it has one.
  const y1 = H - (spec.axis.label ? 40 : 18);
  if (kind === 'ordinal') {
    // One gridline per distinct present position (+ endpoints); labels only if the
    // spec supplies story-beat ticks (raw ordinals are meaningless to a reader).
    // The padded ends get a line only when there are no labelled places to
    // frame the plot; with ticks, an unlabelled line at either end reads as a
    // place nothing holds.
    const positions = new Set<number>(spec.axis.ticks?.length ? [] : [t0, t1]);
    for (const tr of spec.tracks)
      for (const s of tr.segments) {
        positions.add(toNum(s.from));
        if (s.to != null) positions.add(toNum(s.to));
      }
    for (const ev of spec.events) positions.add(toNum(ev.date));
    const tickLabel = new Map<number, string>();
    for (const tk of spec.axis.ticks ?? []) tickLabel.set(tk.at, tk.label);
    for (const p of positions) {
      const xx = x(p);
      els.push(<line key={key()} x1={xx} y1={y0} x2={xx} y2={y1} stroke={GRID} strokeWidth={1} />);
      const lbl = tickLabel.get(p);
      if (lbl) {
        els.push(
          <text key={key()} x={xx} y={y1 + 13} fill={MUTED} fontSize={12} fontWeight={600} textAnchor="middle">
            {lbl}
          </text>,
        );
      }
    }
    if (spec.axis.label) {
      els.push(
        <text key={key()} x={L + plotW / 2} y={y1 + 30} fill={MUTED} fontSize={11} textAnchor="middle">
          {spec.axis.label}
        </text>,
      );
    }
  } else {
    for (let n = Math.ceil(t0); n <= t1; n++) {
      const date = `${Math.floor(n / 12)}-${String((n % 12) + 1).padStart(2, '0')}-01`;
      const xx = x(date);
      const isYear = n % 12 === 0;
      els.push(
        <line
          key={key()}
          x1={xx}
          y1={y0}
          x2={xx}
          y2={y1}
          stroke={GRID}
          strokeWidth={1}
          opacity={isYear ? 1 : 0.5}
        />,
      );
      if (isYear) {
        els.push(
          <text key={key()} x={xx + 3} y={y1 + 13} fill={MUTED} fontSize={12} fontWeight={600}>
            {Math.floor(n / 12)}
          </text>,
        );
      }
    }
  }

  // ---- era bands (behind rows) + era labels ----
  for (const lane of spec.lanes) {
    const rows = byLane(lane.id);
    const rowsTop = laneTop(lane.id) + LANE_HEAD;
    const top = rowsTop - 5;
    const bot = rows.length > 0 ? (laneBottom.get(lane.id) ?? rowsTop) : rowsTop;
    for (const era of spec.eras.filter((e) => e.laneId === lane.id)) {
      const xa = x(era.from);
      const xb = x(era.to);
      const col = ERA[era.color] ?? CHART.muted;
      els.push(
        <rect
          key={key()}
          x={xa}
          y={top}
          width={Math.max(1, xb - xa)}
          height={bot - top}
          fill={col}
          opacity={0.1}
        />,
      );
      els.push(
        <text
          key={key()}
          x={xa + 6}
          y={laneTop(lane.id) + 40}
          fill={col}
          fontSize={11}
          fontWeight={700}
          style={{ letterSpacing: '0.3px', textTransform: 'uppercase' }}
        >
          {era.label}
        </text>,
      );
    }
  }

  // ---- lane headers + member rows + bars ----
  for (const lane of spec.lanes) {
    els.push(
      <text key={key()} x={14} y={laneTop(lane.id) + 20} fill={INK} fontSize={13} fontWeight={600}>
        {lane.label}
      </text>,
    );
    byLane(lane.id).forEach((t, ti) => {
      const ry0 = rowTop(lane.id, idOf(t, ti));
      // Bars and dots sit on the row's midline; `ry` is where a one-line
      // row's 24-pixel band starts, so a taller row centres the same band.
      const mid = ry0 + rowHeight(t) / 2;
      const ry = mid - 12;
      // Row label: one line normally; two lines (title + issue/date annotation)
      // when the track carries an `issue` note — keeps it out of the plot area.
      if (t.issue) {
        els.push(
          <text key={key()} x={L - 12} y={mid - 3} fill={INK} fontSize={12.5} textAnchor="end">
            {t.label}
          </text>,
        );
        els.push(
          <text key={key()} x={L - 12} y={mid + 11} fill={MUTED} fontSize={10} textAnchor="end">
            {t.issue}
          </text>,
        );
      } else {
        els.push(
          <text key={key()} x={L - 12} y={ry + 16} fill={INK} fontSize={12.5} textAnchor="end">
            {t.label}
          </text>,
        );
      }
      for (const seg of t.segments) {
        // A point (a place in story order, a single date) is a dot, a
        // dashed ring when its place was inferred, never a sliver of a bar.
        if (seg.point) {
          const col = CHART.bar;
          els.push(
            seg.derived ? (
              <circle key={key()} cx={x(seg.from)} cy={mid} r={7} fill="none" stroke={col} strokeWidth={2} strokeDasharray="3 2" />
            ) : (
              <circle key={key()} cx={x(seg.from)} cy={mid} r={7} fill={col} />
            ),
          );
          continue;
        }
        const xa = seg.openStart ? L : x(seg.from);
        const xb = seg.to != null ? x(seg.to) : x(spec.axis.to);
        const era = eraFor(lane.id, seg.from);
        const col = era ? (ERA[era.color] ?? CHART.muted) : CHART.bar;
        const open = seg.to == null;
        const derived = !!seg.derived;
        els.push(
          <rect
            key={key()}
            x={xa}
            y={ry + 4}
            width={Math.max(3, xb - xa)}
            height={16}
            rx={7}
            fill={col}
            opacity={derived ? 0.45 : open ? 0.95 : 0.85}
            stroke={derived ? MUTED : 'none'}
            {...(derived ? { strokeDasharray: '4 3' } : {})}
          />,
        );
        if (open) {
          els.push(
            <path key={key()} d={`M${xb - 2},${ry + 4} l10,8 l-10,8 z`} fill={col} opacity={0.95} />,
          );
        }
        if (seg.openStart) {
          els.push(
            <path key={key()} d={`M${xa + 2},${ry + 4} l-10,8 l10,8 z`} fill={col} opacity={0.7} />,
          );
        }
      }
    });
  }

  // ---- event markers (milestone stories) across full height ----
  // Story-time labels are long, so stagger more aggressively and right-anchor any
  // label near the right edge so it reads leftward instead of clipping.
  const stag = kind === 'ordinal' ? 200 : 108;
  const placed: { x: number; y: number }[] = [];
  for (const ev of spec.events) {
    const xx = x(ev.date);
    els.push(
      <line
        key={key()}
        x1={xx}
        y1={y0}
        x2={xx}
        y2={y1}
        stroke={MUTED}
        strokeDasharray="3 3"
        strokeWidth={1}
        opacity={0.55}
      />,
    );
    let ly = y0 - 16;
    while (placed.some((p) => Math.abs(p.x - xx) < stag && Math.abs(p.y - ly) < 26)) ly -= 26;
    placed.push({ x: xx, y: ly });
    const nearRight = xx > W - 240;
    const tx = nearRight ? xx - 4 : xx + 4;
    const anchor = nearRight ? 'end' : 'start';
    els.push(
      <text key={key()} x={tx} y={ly} textAnchor={anchor} fill={MUTED} fontSize={11}>
        {ev.label}
      </text>,
    );
    if (ev.issue) {
      els.push(
        <text key={key()} x={tx} y={ly + 12} textAnchor={anchor} fill={MUTED} fontSize={10} opacity={0.8}>
          {ev.issue}
        </text>,
      );
    }
  }

  // ---- crossover: a member appearing in two lanes, end-date == start-date ----
  const byMember = new Map<string, Track[]>();
  for (const t of spec.tracks) {
    const arr = byMember.get(t.label) ?? [];
    arr.push(t);
    byMember.set(t.label, arr);
  }
  for (const [label, ts] of byMember) {
    if (ts.length < 2) continue;
    for (const a of ts)
      for (const b of ts) {
        if (a === b) continue;
        const aEnd = a.segments.at(-1)?.to ?? null;
        const bStart = b.segments[0]?.from;
        if (aEnd == null || bStart == null || aEnd !== bStart) continue;
        const ay = rowTop(a.laneId, a.id ?? label);
        const by = rowTop(b.laneId, b.id ?? label);
        const xx = x(aEnd);
        const fromY = ay + rowHeight(a) / 2;
        const toY = by + rowHeight(b) / 2;
        const midX = xx + Math.max(30, Math.abs(fromY - toY) * 0.4);
        els.push(
          <path
            key={key()}
            d={`M${xx},${fromY} C${midX},${fromY} ${midX},${toY} ${xx},${toY}`}
            fill="none"
            stroke={CHART.cross}
            strokeWidth={2.5}
            opacity={0.95}
          />,
        );
        els.push(<circle key={key()} cx={xx} cy={fromY} r={3.5} fill={CHART.cross} />);
        els.push(<circle key={key()} cx={xx} cy={toY} r={3.5} fill={CHART.cross} />);
      }
  }

  return { svg: els, width: W, height: H };
}
